import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { ensureAdminRole, checkIsAdmin } from "@/lib/admin.functions";
import { toast } from "sonner";
import {
  Loader2,
  LogOut,
  Image as ImageIcon,
  Users as UsersIcon,
  FolderTree,
  Package,
  Inbox,
  FileEdit,
  Download,
} from "lucide-react";
import { inp } from "@/components/admin/shared";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminLayout,
});

function AdminLayout() {
  const [session, setSession] = useState<any>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [checkingAdmin, setCheckingAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const ensure = useServerFn(ensureAdminRole);
  const check = useServerFn(checkIsAdmin);

  useEffect(() => {
    let active = true;
    const authTimeout = window.setTimeout(() => {
      if (active) setAuthLoading(false);
    }, 1500);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_evt, sess) => {
      if (!active) return;
      setSession(sess);
      if (!sess) {
        setIsAdmin(false);
        setCheckingAdmin(false);
      }
      setAuthLoading(false);
    });

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!active) return;
        setSession(data.session);
        if (!data.session) setIsAdmin(false);
      })
      .catch((e: any) => {
        if (active) toast.error(e.message ?? "Kunde inte läsa inloggningen.");
      })
      .finally(() => {
        if (active) setAuthLoading(false);
      });

    return () => {
      active = false;
      window.clearTimeout(authTimeout);
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session) return;
    let active = true;
    setCheckingAdmin(true);
    ensure({})
      .then(() => check({}))
      .then((r) => {
        if (active) setIsAdmin(r.isAdmin);
      })
      .catch((e: any) => {
        if (active) toast.error(e.message ?? "Kunde inte kontrollera admin-behörighet.");
      })
      .finally(() => {
        if (active) setCheckingAdmin(false);
      });
    return () => {
      active = false;
    };
  }, [session?.access_token]);

  if (authLoading)
    return (
      <Center>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </Center>
    );
  if (!session) return <AuthForm />;
  if (checkingAdmin)
    return (
      <Center>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </Center>
    );
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-md px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold">Inloggad — men inte admin</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Det här kontot saknar admin-behörighet.
        </p>
        <button
          onClick={() => supabase.auth.signOut()}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-secondary px-5 py-2.5 text-sm font-semibold"
        >
          <LogOut className="h-4 w-4" /> Logga ut
        </button>
      </div>
    );
  }
  return <AdminShell email={session.user?.email ?? ""} />;
}

function Center({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">{children}</div>
  );
}

function AuthForm() {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/admin` },
        });
        if (error) throw error;
        toast.success("Konto skapat. Du loggas in automatiskt.");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-20">
      <h1 className="font-display text-3xl font-extrabold">Admin</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {mode === "login" ? "Logga in för att hantera innehåll." : "Skapa konto."}
      </p>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">
            E-post
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inp}
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">
            Lösenord
          </label>
          <input
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inp}
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-3 font-bold disabled:opacity-50"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {mode === "login" ? "Logga in" : "Skapa konto"}
        </button>
        <button
          type="button"
          onClick={() => setMode(mode === "login" ? "signup" : "login")}
          className="w-full text-sm text-muted-foreground hover:text-foreground"
        >
          {mode === "login" ? "Inget konto? Skapa ett" : "Har redan konto? Logga in"}
        </button>
      </form>
    </div>
  );
}

function AdminShell({ email }: { email: string }) {
  const loc = useLocation();
  const tabs = [
    { to: "/admin/support", l: "Support", icon: Inbox },
    { to: "/admin/inquiries", l: "Förfrågningar", icon: Inbox },
    { to: "/admin/inbox", l: "Inkorg", icon: Inbox },
    { to: "/admin/drafts", l: "Påbörjade", icon: FileEdit },
    { to: "/admin/products", l: "Produkter", icon: Package },
    { to: "/admin/import", l: "Importera", icon: Download },
    { to: "/admin/media", l: "Media", icon: ImageIcon },
    { to: "/admin/categories", l: "Kategorier", icon: FolderTree },
    { to: "/admin/users", l: "Användare", icon: UsersIcon },
  ] as const;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:py-10">
      <div className="flex items-start sm:items-center justify-between mb-6 sm:mb-8 gap-3 flex-col sm:flex-row">
        <div className="min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold">Admin</h1>
          <p className="text-xs sm:text-sm text-muted-foreground break-all">{email}</p>
        </div>
        <button
          onClick={() => supabase.auth.signOut()}
          className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-2 text-sm font-semibold hover:bg-secondary/70 self-end sm:self-auto"
        >
          <LogOut className="h-4 w-4" /> Logga ut
        </button>
      </div>

      <div className="grid grid-cols-2 sm:flex gap-1 mb-6 sm:mb-8 sm:border-b sm:border-border sm:overflow-x-auto">
        {tabs.map((t) => {
          const active = loc.pathname.startsWith(t.to);
          return (
            <Link
              key={t.to}
              to={t.to}
              className={`px-3 sm:px-4 py-3 sm:py-2.5 text-sm font-semibold rounded-lg sm:rounded-none sm:border-b-2 sm:-mb-px transition inline-flex items-center justify-center sm:justify-start gap-2 whitespace-nowrap ${
                active
                  ? "bg-primary/10 text-primary sm:bg-transparent sm:border-primary"
                  : "bg-secondary/60 text-muted-foreground hover:text-foreground sm:bg-transparent sm:border-transparent"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.l}
            </Link>
          );
        })}
      </div>

      <Outlet />
    </div>
  );
}

